"use client";

interface PasswordStrengthIndicatorProps {
  password: string;
}

function getStrength(password: string): { score: number; label: string; color: string; bars: number } {
  if (!password) return { score: 0, label: "", color: "", bars: 0 };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 2) return { score, label: "Weak", color: "bg-red-500", bars: 1 };
  if (score <= 3) return { score, label: "Medium", color: "bg-accent-gold", bars: 2 };
  return { score, label: "Strong", color: "bg-accent-emerald", bars: 3 };
}

export default function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = getStrength(password);

  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1.5 mb-1.5">
        {[1, 2, 3].map((bar) => (
          <div
            key={bar}
            className={`h-1 flex-1 rounded-full transition-all duration-500 ${
              bar <= strength.bars ? strength.color : "bg-white/10"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between">
        <p className={`text-[11px] font-mono ${
          strength.label === "Weak" ? "text-red-400" :
          strength.label === "Medium"? "text-accent-gold" : "text-accent-emerald"
        }`}>
          {strength.label} password
        </p>
        {strength.label === "Weak" && (
          <p className="text-[10px] text-gray-600">Add uppercase, numbers & symbols</p>
        )}
        {strength.label === "Medium" && (
          <p className="text-[10px] text-gray-600">Add symbols for stronger security</p>
        )}
        {strength.label === "Strong" && (
          <p className="text-[10px] text-gray-600">Great password!</p>
        )}
      </div>
    </div>
  );
}
